import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { apiRequest, streamRequest } from '../services/api'
import { pb } from '../services/pocketbase'
import type { ChatMessage, Citation, Conversation } from '../types'

interface EvaluationResult {
  ok: boolean
  choice: string
  ab_status: Conversation['ab_status']
  requires_retry: boolean
  terminated: boolean
  ab_round?: number
  next_ab_round?: number
  ab_max_rounds?: number
}

function requestId(): string {
  return `web:${crypto.randomUUID()}`
}

export const useChatStore = defineStore('chat', () => {
  const conversations = ref<Conversation[]>([])
  const currentConversation = ref<Conversation | null>(null)
  const messages = ref<ChatMessage[]>([])
  const loading = ref(false)
  const streaming = ref(false)
  const error = ref('')
  const providerMessage = ref('')
  let unsubscribeConversation: (() => void) | null = null
  let unsubscribeMessages: (() => void) | null = null
  const attemptedABRetries = new Set<string>()

  const requiresEvaluation = computed(
    () => currentConversation.value?.ab_status === 'awaiting_evaluation',
  )
  const needsABRetry = computed(
    () => currentConversation.value?.ab_status === 'retry_pending',
  )
  const isTerminated = computed(
    () => currentConversation.value?.ab_status === 'terminated',
  )
  const isIncomplete = computed(
    () => currentConversation.value?.ab_status === 'incomplete',
  )
  const needsProviderRetry = computed(
    () => currentConversation.value?.ab_status === 'provider_retry_pending',
  )
  const canContinue = computed(() => !!currentConversation.value?.can_continue && !streaming.value)
  function renamed(record: Conversation) {
    const index=conversations.value.findIndex(c=>c.id===record.id)
    if(index>=0) conversations.value[index]=record
    if(currentConversation.value?.id===record.id) currentConversation.value=record
  }
  async function continueConversation() {
    if(!currentConversation.value||streaming.value)return
    const id=currentConversation.value.id
    error.value=''
    try {
      renamed(await apiRequest<Conversation>(`/v1/conversations/${id}/continue`,{method:'POST',body:JSON.stringify({request_id:requestId()})}))
      await openConversation(id)
    } catch(e){error.value=e instanceof Error?e.message:'暂时无法继续，请稍后重试。'}
  }
  const inputBlocked = computed(
    () =>
      requiresEvaluation.value ||
      needsABRetry.value ||
      currentConversation.value?.ab_status === 'retry_generating' ||
      needsProviderRetry.value ||
      currentConversation.value?.ab_status === 'provider_retry_generating' ||
      isIncomplete.value ||
      isTerminated.value,
  )
  const latestCitations = computed<Citation[]>(() => {
    const assistant = [...messages.value]
      .reverse()
      .find((message) => message.role === 'assistant' && message.citations?.length)
    return assistant?.citations || []
  })

  async function loadConversations() {
    const records = await apiRequest<Conversation[]>('/v1/conversations')
    conversations.value = records
    if (currentConversation.value) {
      currentConversation.value =
        records.find((item) => item.id === currentConversation.value?.id) ||
        currentConversation.value
    }
  }

  async function createConversation(title = '新对话') {
    const conversation = await apiRequest<Conversation>('/v1/conversations', {
      method: 'POST',
      body: JSON.stringify({ title }),
    })
    conversations.value.unshift(conversation)
    await openConversation(conversation.id)
    return conversation
  }

  async function openConversation(id: string) {
    await loadConversations()
    currentConversation.value =
      conversations.value.find((item) => item.id === id) || null
    messages.value = await apiRequest<ChatMessage[]>(
      `/v1/conversations/${id}/messages`,
    )
    await subscribe(id)
    const retryKey = `${id}:${currentConversation.value?.message_count || 0}:${currentConversation.value?.ab_round || 0}`
    if (
      currentConversation.value?.ab_status === 'retry_pending' &&
      !streaming.value &&
      !attemptedABRetries.has(retryKey)
    ) {
      attemptedABRetries.add(retryKey)
      await retryAB()
    }
  }

  async function subscribe(id: string) {
    unsubscribeConversation?.()
    unsubscribeMessages?.()
    unsubscribeConversation = await pb
      .collection('conversations')
      .subscribe(id, (event) => {
        const realtimeRecord = event.record as unknown as Partial<Conversation>
        if (currentConversation.value?.id === id) {
          currentConversation.value = {
            ...currentConversation.value,
            ...realtimeRecord,
          }
        }
        const index = conversations.value.findIndex(
          (item) => item.id === event.record.id,
        )
        if (index >= 0) {
          conversations.value[index] = {
            ...conversations.value[index],
            ...realtimeRecord,
          }
        }
      })
    unsubscribeMessages = await pb
      .collection('chat_history')
      .subscribe('*', (event) => {
        const record = event.record as unknown as ChatMessage
        if (record.conversation !== id || streaming.value) return
        const index = messages.value.findIndex((item) => item.id === record.id)
        if (event.action === 'delete') {
          if (index >= 0) messages.value.splice(index, 1)
        } else if (index >= 0) {
          messages.value[index] = record
        } else {
          messages.value.push(record)
        }
      })
  }

  async function sendMessage(content: string) {
    if (!currentConversation.value || streaming.value) return
    loading.value = true
    streaming.value = true
    error.value = ''
    providerMessage.value = ''
    const conversationId = currentConversation.value.id
    const turn = (currentConversation.value.message_count || 0) + 1
    const optimisticUser: ChatMessage = {
      id: `local-user-${Date.now()}`,
      conversation: conversationId,
      turn,
      role: 'user',
      content,
      citations: [],
      streaming: false,
    }
    messages.value.push(optimisticUser)
    const streamAnswers: Record<string, ChatMessage> = {}
    const id = requestId()
    try {
      await streamRequest(
        `/v1/conversations/${conversationId}/messages`,
        { message: content, request_id: id },
        ({ event, data }) => {
          if (event === 'meta') {
            const isAB = Boolean(data.ab)
            const labels = isAB ? ['A', 'B'] : ['assistant']
            for (const label of labels) {
              const item: ChatMessage = {
                id: `stream-${label}-${Date.now()}`,
                conversation: conversationId,
                turn,
                role: 'assistant',
                public_label: label === 'assistant' ? '' : (label as 'A' | 'B'),
                content: '',
                citations: (data.citations as Citation[]) || [],
                retrieval_mode: data.mode as 'rag' | 'pure_llm',
                ab_round: Number(data.ab_round || 0) || undefined,
                streaming: true,
              }
              streamAnswers[label] = item
              messages.value.push(item)
            }
          } else if (event === 'queue_status') {
            const state = String(data.state || '')
            if (state === 'queued') {
              const position = Number(data.position || 0)
              providerMessage.value = position > 0
                ? `请求正在排队（前方约 ${position - 1} 个任务）。`
                : '请求正在排队。'
            } else if (state === 'running') {
              providerMessage.value = '已获得模型容量，正在生成回答…'
            } else if (state === 'preparing') {
              providerMessage.value = '正在检查问题并检索教材…'
            } else if (state === 'degraded') {
              providerMessage.value = String(
                data.message || '教材检索暂时不可用，正在切换到拓展回答模式…',
              )
            }
          } else if (event === 'heartbeat') {
            providerMessage.value=String(data.message||'仍在等待模型…')
          } else if (event === 'provider_retry') {
            providerMessage.value = `模型服务繁忙，正在等待后重试（第 ${Number(data.attempt || 1)} 次）…`
          } else if (event === 'answer_delta') {
            providerMessage.value = ''
            const label = String(data.label)
            if (streamAnswers[label]) {
              streamAnswers[label].content += String(data.delta || '')
            }
          } else if (event === 'answer_complete') {
            const label = String(data.label)
            if (streamAnswers[label]) {
              streamAnswers[label].id = String(data.message_id)
              streamAnswers[label].streaming = false
              streamAnswers[label].citations = (data.citations as Citation[]) || []
            }
          } else if (event === 'conversation_title') {
            const title = String(data.title || '').trim()
            if (title && currentConversation.value) {
              currentConversation.value.title = title
              const index = conversations.value.findIndex(
                (item) => item.id === conversationId,
              )
              if (index >= 0) conversations.value[index].title = title
            }
          } else if (
            event === 'answer_error' ||
            event === 'fatal_error' ||
            event === 'provider_busy'
          ) {
            error.value = String(data.message || '回答生成失败。')
          } else if (event === 'done') {
            if (currentConversation.value) {
              currentConversation.value.message_count = turn
              if (!data.scope_rejected && !data.scope_error) {
                currentConversation.value.ab_status = Boolean(
                  data.requires_evaluation,
                )
                  ? 'awaiting_evaluation'
                  : data.provider_retry_pending
                    ? 'provider_retry_pending'
                    : 'active'
              }
            }
          }
        },
      )
    } catch (reason) {
      error.value =
        reason instanceof Error ? reason.message : '发送失败，请稍后重试。'
      messages.value = messages.value.filter(
        (message) =>
          message.id !== optimisticUser.id &&
          !message.id.startsWith('stream-'),
      )
      throw reason
    } finally {
      loading.value = false
      streaming.value = false
      await openConversation(conversationId)
      await loadConversations()
    }
  }

  async function retryProviderGeneration() {
    if (!currentConversation.value || streaming.value || !needsProviderRetry.value) {
      return
    }
    const conversationId = currentConversation.value.id
    const turn = currentConversation.value.message_count
    loading.value = true
    streaming.value = true
    error.value = ''
    providerMessage.value = '正在按原模型和原问题重试…'
    const streamAnswers: Record<string, ChatMessage> = {}
    try {
      await streamRequest(
        `/v1/conversations/${conversationId}/generation-retry`,
        { request_id: requestId() },
        ({ event, data }) => {
          if (event === 'meta') {
            const labels = Boolean(data.ab) ? ['A', 'B'] : ['assistant']
            for (const label of labels) {
              const existing=messages.value.find(m=>m.turn===turn && (m.ab_round||0)===Number(data.ab_round||0) && (m.public_label||'assistant')===label && m.status==='complete')
              if(existing) continue
              messages.value=messages.value.filter(m=>!(m.turn===turn && (m.ab_round||0)===Number(data.ab_round||0) && (m.public_label||'assistant')===label && m.status==='failed'))
              const item: ChatMessage = {
                id: `stream-provider-${label}-${Date.now()}`,
                conversation: conversationId,
                turn,
                role: 'assistant',
                public_label: label === 'assistant' ? '' : (label as 'A' | 'B'),
                ab_round: Number(data.ab_round || 0) || undefined,
                content: '',
                citations: (data.citations as Citation[]) || [],
                retrieval_mode: data.mode as 'rag' | 'pure_llm',
                streaming: true,
              }
              streamAnswers[label] = item
              messages.value.push(item)
            }
            if (currentConversation.value) {
              currentConversation.value.ab_status = 'provider_retry_generating'
            }
          } else if (event === 'queue_status') {
            const position = Number(data.position || 0)
            providerMessage.value = position > 0
              ? `原模型正在排队（前方约 ${position - 1} 个任务）…`
              : '正在连接原模型…'
          } else if (event === 'heartbeat') {
            providerMessage.value=String(data.message||'仍在等待模型…')
          } else if (event === 'provider_retry') {
            providerMessage.value = `原模型仍繁忙，正在进行第 ${Number(data.attempt || 1)} 次重试…`
          } else if (event === 'answer_delta') {
            providerMessage.value = ''
            const label = String(data.label)
            if (streamAnswers[label]) {
              streamAnswers[label].content += String(data.delta || '')
            }
          } else if (event === 'answer_complete') {
            const label = String(data.label)
            if (streamAnswers[label]) {
              streamAnswers[label].id = String(data.message_id)
              streamAnswers[label].streaming = false
            }
          } else if (
            event === 'answer_error' ||
            event === 'fatal_error' ||
            event === 'provider_busy'
          ) {
            error.value = String(data.message || '原模型重试失败。')
          } else if (event === 'done' && currentConversation.value) {
            currentConversation.value.ab_status = data.provider_retry_pending
              ? 'provider_retry_pending'
              : Boolean(data.requires_evaluation)
                ? 'awaiting_evaluation'
                : 'active'
          }
        },
      )
    } catch (reason) {
      error.value = reason instanceof Error ? reason.message : '原模型重试失败。'
      throw reason
    } finally {
      loading.value = false
      streaming.value = false
      providerMessage.value = ''
      await openConversation(conversationId)
      await loadConversations()
    }
  }

  async function evaluate(choice: string, comment = '') {
    if (!currentConversation.value) return
    const result = await apiRequest<EvaluationResult>(
      `/v1/conversations/${currentConversation.value.id}/evaluation`,
      {
        method: 'POST',
        body: JSON.stringify({ choice, comment }),
      },
    )
    currentConversation.value.ab_status = result.ab_status
    if (result.ab_round) currentConversation.value.ab_round = result.ab_round
    if (result.ab_max_rounds) {
      currentConversation.value.ab_max_rounds = result.ab_max_rounds
    }
    if (result.requires_retry) {
      await retryAB()
    }
    await loadConversations()
    return result
  }

  async function saveMessageFeedback(
    messageId: string,
    reaction: '' | 'like' | 'dislike',
    comment: string,
  ) {
    if (!currentConversation.value) return
    error.value = ''
    try {
      const updated = await apiRequest<ChatMessage>(
        `/v1/conversations/${currentConversation.value.id}/messages/${messageId}/feedback`,
        {
          method: 'PUT',
          body: JSON.stringify({ reaction, comment }),
        },
      )
      const index = messages.value.findIndex((item) => item.id === messageId)
      if (index >= 0) {
        messages.value[index] = { ...messages.value[index], ...updated }
      }
      return updated
    } catch (reason) {
      error.value = reason instanceof Error ? reason.message : '评价保存失败。'
      throw reason
    }
  }

  async function retryAB() {
    if (!currentConversation.value || streaming.value) return
    const conversationId = currentConversation.value.id
    attemptedABRetries.add(
      `${conversationId}:${currentConversation.value.ab_round || 0}`,
    )
    const turn = currentConversation.value.message_count
    loading.value = true
    streaming.value = true
    error.value = ''
    const streamAnswers: Record<string, ChatMessage> = {}
    try {
      await streamRequest(
        `/v1/conversations/${conversationId}/ab-retry`,
        { request_id: requestId() },
        ({ event, data }) => {
          if (event === 'meta') {
            const round = Number(data.ab_round || 0) || undefined
            for (const label of ['A', 'B']) {
              const item: ChatMessage = {
                id: `stream-retry-${label}-${Date.now()}`,
                conversation: conversationId,
                turn,
                role: 'assistant',
                public_label: label as 'A' | 'B',
                ab_round: round,
                content: '',
                citations: (data.citations as Citation[]) || [],
                retrieval_mode: data.mode as 'rag' | 'pure_llm',
                streaming: true,
              }
              streamAnswers[label] = item
              messages.value.push(item)
            }
            if (currentConversation.value) {
              currentConversation.value.ab_status = 'retry_generating'
              currentConversation.value.ab_round = round
              currentConversation.value.ab_max_rounds = Number(
                data.ab_max_rounds || currentConversation.value.ab_max_rounds,
              )
            }
          } else if (event === 'answer_delta') {
            const label = String(data.label)
            if (streamAnswers[label]) {
              streamAnswers[label].content += String(data.delta || '')
            }
          } else if (event === 'answer_complete') {
            const label = String(data.label)
            if (streamAnswers[label]) {
              streamAnswers[label].id = String(data.message_id)
              streamAnswers[label].streaming = false
              streamAnswers[label].citations = (data.citations as Citation[]) || []
            }
          } else if (event === 'answer_error' || event === 'fatal_error') {
            error.value = String(data.message || '下一组回答生成失败。')
          } else if (event === 'done' && currentConversation.value) {
            currentConversation.value.ab_status = Boolean(data.requires_evaluation)
              ? 'awaiting_evaluation'
              : 'incomplete'
            currentConversation.value.ab_round = Number(
              data.ab_round || currentConversation.value.ab_round,
            )
          }
        },
      )
    } catch (reason) {
      error.value =
        reason instanceof Error ? reason.message : '下一组回答生成失败。'
      throw reason
    } finally {
      loading.value = false
      streaming.value = false
      await openConversation(conversationId)
      await loadConversations()
    }
  }

  function dispose() {
    unsubscribeConversation?.()
    unsubscribeMessages?.()
  }

  return {
    conversations,
    currentConversation,
    messages,
    loading,
    streaming,
    error,
    requiresEvaluation,
    needsABRetry,
    isTerminated,
    isIncomplete,
    needsProviderRetry,
    canContinue,
    continueConversation,
    renamed,
    inputBlocked,
    providerMessage,
    latestCitations,
    loadConversations,
    createConversation,
    openConversation,
    sendMessage,
    evaluate,
    saveMessageFeedback,
    retryProviderGeneration,
    dispose,
  }
})
